/**
 * <TutorQualityAlerts> — les alertes de la clôture qualité d'un live, côté masteur (molecule).
 *
 * L'écran d'aujourd'hui les dit de deux façons, et la molécule garde les deux :
 *  - **ouvertes** (`collapsible={false}`, « Les bases ») : pour chaque alerte, sa chip — la règle
 *    de la charte pour un manquement, sinon le libellé de l'indicateur —, la chip « 2ème session
 *    consécutive » quand elle revient, puis son message (`AlertRow` de `QualityClosureScreen`) ;
 *  - **repliables** (`collapsible`, la carte d'un élève) : les chips en rangée, et chacune déplie
 *    son message dessous (`CollapsibleAxisAlerts` de `StudentCloseOutCard`).
 * Un manquement à la charte est rouge, un point d'attention orange, dans les deux.
 *
 * Quelle chip est dépliée est un état de vue : il vit ici (`defaultOpen` pour une story).
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { textInkOnFill } from "../../theme/tokens/colors"
import { type UITutorQualityAlert } from "../../../ports/primary/ui/types/tutorCloseOut"

export type TutorQualityAlertsProps = {
    alerts: UITutorQualityAlert[]
    /** Les chips en rangée, chacune dépliant son message. Défaut `false` : tout est ouvert. */
    collapsible?: boolean
    /** `collapsible` : les indices des chips dépliées à l'ouverture. Défaut : aucune. */
    defaultOpen?: number[]
    testID?: string
}

/** La 2ᵉ fois d'affilée, et au-delà : la chip qui le dit. */
const RECURRENCE_FROM = 2

function AlertChip(props: { alert: UITutorQualityAlert }) {
    const { alert } = props
    return (
        <Badge
            text={alert.label}
            variant={alert.charterBreach ? "danger" : "warning"}
            tinted
            iconName={alert.charterBreach ? "balance-scale" : "exclamation-triangle"}
        />
    )
}

function RecurrenceChip(props: { count?: number }) {
    if (props.count == null || props.count < RECURRENCE_FROM) return null
    return <Badge text={`${props.count}ème session consécutive`} variant="danger" tinted iconName="redo" />
}

function AlertMessage(props: { alert: UITutorQualityAlert }) {
    const { alert } = props
    return (
        <Text size="text-sm" color={alert.charterBreach ? textInkOnFill("danger") : "text-muted"}>
            {alert.message}
        </Text>
    )
}

export function TutorQualityAlerts(props: TutorQualityAlertsProps) {
    const { alerts, collapsible = false, defaultOpen, testID } = props
    const [open, setOpen] = React.useState<number[]>(defaultOpen ?? [])

    if (!collapsible) {
        return (
            <Stack gap="3" testID={testID}>
                {alerts.map((alert, index) => (
                    <Stack key={index} gap="1">
                        <Row gap="1" style={styles.wrap}>
                            <AlertChip alert={alert} />
                            <RecurrenceChip count={alert.recurrenceCount} />
                        </Row>
                        <AlertMessage alert={alert} />
                    </Stack>
                ))}
            </Stack>
        )
    }

    const toggle = (index: number) =>
        setOpen((current) => (current.includes(index) ? current.filter((i) => i !== index) : [...current, index]))

    return (
        <Stack gap="2" testID={testID}>
            <Row gap="2" style={styles.wrap}>
                {alerts.map((alert, index) => {
                    const isOpen = open.includes(index)
                    return (
                        <Pressable
                            key={index}
                            onPress={() => toggle(index)}
                            accessibilityRole="button"
                            accessibilityState={{ expanded: isOpen }}
                        >
                            <Row gap="1" align="center">
                                <AlertChip alert={alert} />
                                <Icon
                                    name={isOpen ? "chevron-up" : "chevron-down"}
                                    size={16}
                                    color={alert.charterBreach ? textInkOnFill("danger") : textInkOnFill("warning")}
                                />
                            </Row>
                        </Pressable>
                    )
                })}
            </Row>
            {alerts.map((alert, index) =>
                open.includes(index) ? (
                    <View key={`message-${index}`} style={styles.message}>
                        <Stack gap="1">
                            {alert.recurrenceCount != null && alert.recurrenceCount >= RECURRENCE_FROM && (
                                <Row>
                                    <RecurrenceChip count={alert.recurrenceCount} />
                                </Row>
                            )}
                            <AlertMessage alert={alert} />
                        </Stack>
                    </View>
                ) : null
            )}
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    wrap: {
        flexWrap: "wrap",
    },
    /** L'encart du message, sans liseré : la chip au-dessus en dit déjà la couleur. */
    message: {
        backgroundColor: theme.colors["surface-subtle"],
        borderRadius: theme.radius.sm,
        paddingVertical: theme.spacing["2"],
        paddingHorizontal: theme.spacing["3"],
    },
}))
